import { useState } from 'react';

import { Spinner } from '@provider-portal/common/components/Spinner/Spinner';

import { Alert, Button, Checkbox, Field, Input } from '@bolteu/clay-react';
import { NetworkRequestState } from '@bolteu/redux-session';

import { LoginErrorType } from 'root/screens/Login/Login';

interface LoginFormFields {
  username: string;
  password: string;
}

interface LoginFormProps {
  strings: Record<
    'wrongCredentials' | 'accountLocked' | 'username' | 'password' | 'rememberMe' | 'login',
    string
  >;
  onSubmit: (payload: LoginFormFields) => void;
  authError: LoginErrorType | undefined;
  authRequestState: NetworkRequestState | undefined;
  accountRequestState: NetworkRequestState;
}

export function LoginForm({
  strings,
  onSubmit,
  authError,
  authRequestState,
  accountRequestState,
}: LoginFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit({ username, password });
  };

  const authErrorString = () => {
    if (!authError) {
      return undefined;
    }
    switch (authError) {
      case LoginErrorType.BAD_CREDENTIALS:
        return strings.wrongCredentials;
      case LoginErrorType.ACCOUNT_LOCKED:
        return strings.accountLocked;
      default:
        return strings.wrongCredentials;
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="login-form">
      {authError ? (
        <Alert.Root severity="error" className="my-4">
          <Alert.Icon />
          <Alert.Content>
            <Alert.Description>{authErrorString()}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
      <Field.Root className="mt-4 mb-2">
        <Field.Label>{strings.username}</Field.Label>
        <Input
          fieldSize="lg"
          id="username"
          name="username"
          autoComplete="username"
          autoFocus
          onValueChange={setUsername}
        />
      </Field.Root>
      <Field.Root className="mt-4 mb-2">
        <Field.Label>{strings.password}</Field.Label>
        <Input
          fieldSize="lg"
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          onValueChange={setPassword}
        />
      </Field.Root>
      <div className="mt-4 mb-2">
        <label className="bolt-font-body-m-regular -ml-[11px] mr-4 inline-flex items-center">
          <span className="flex size-[42px] items-center justify-center">
            <Checkbox value="remember" />
          </span>
          {strings.rememberMe}
        </label>
      </div>
      <Button type="submit" size="md" className="w-full">
        {authRequestState === NetworkRequestState.waiting ||
        accountRequestState === NetworkRequestState.waiting ? (
          <Spinner />
        ) : (
          <span>{strings.login}</span>
        )}
      </Button>
    </form>
  );
}
